import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';

interface ComebackOfferBannerProps {
  /** True once the user accepted — renders a compact confirmation instead. */
  armed: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}

/**
 * Paywall-abandonment "comeback" offer — shown on plan selection to a
 * returning visitor who looked at plans before, never started checkout, and
 * has no subscription. Warm, low-pressure framing; always dismissible.
 *
 * Once accepted, the discount is threaded into whichever plan the user picks
 * next (not tied to a specific tier here), so this renders a slim
 * confirmation strip instead of disappearing outright — the actual price
 * shown at checkout is Stripe's, which is the source of truth if the backend
 * ends up declining the offer.
 */
export default function ComebackOfferBanner({ armed, onAccept, onDismiss }: ComebackOfferBannerProps) {
  const { t } = useTranslation();

  if (armed) {
    return (
      <View
        className="rounded-2xl px-4 py-3 mb-6 flex-row items-center"
        style={{
          backgroundColor: '#ECFDF5',
          borderWidth: 1,
          borderColor: '#A7F3D0',
          gap: 10,
        }}
      >
        <MaterialIcons name="check-circle" size={18} color="#047857" />
        <Text className="text-sm font-semibold flex-1" style={{ color: '#065F46' }}>
          {t('billing.comebackOffer.appliedBadge')}
        </Text>
      </View>
    );
  }

  return (
    <View
      className="rounded-2xl px-5 py-4 mb-6"
      style={{
        backgroundColor: '#FFF7ED',
        borderWidth: 1,
        borderColor: '#FED7AA',
        gap: 12,
      }}
      accessibilityRole="none"
      testID="comeback-offer-banner"
    >
      <View className="flex-row items-start" style={{ gap: 12 }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: '#FFEDD5',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MaterialIcons name="redeem" size={20} color="#C2410C" />
        </View>
        <View style={{ flex: 1 }}>
          <Text className="text-base font-extrabold" style={{ color: '#7C2D12' }}>
            {t('billing.comebackOffer.title')}
          </Text>
          <Text className="text-sm mt-1" style={{ color: '#9A3412' }}>
            {t(
              'billing.comebackOffer.subtitle',
            )}
          </Text>
        </View>
        <TouchableOpacity
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('billing.comebackOffer.dismissA11y')}
          testID="comeback-offer-dismiss"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <MaterialIcons name="close" size={18} color="#9A3412" />
        </TouchableOpacity>
      </View>
      <TouchableOpacity
        onPress={onAccept}
        accessibilityRole="button"
        testID="comeback-offer-accept"
        style={{
          backgroundColor: '#C2410C',
          borderRadius: 10,
          paddingVertical: 10,
          alignItems: 'center',
        }}
      >
        <Text className="text-sm font-bold" style={{ color: '#FFFFFF' }}>
          {t('billing.comebackOffer.cta')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
